import React, { useEffect } from "react"
import { CircularProgress } from "@mui/material"
import Alert from "@mui/material/Alert"
import { Formik, Form } from "formik"
import { useQuery } from "@apollo/client/react"
import { GetVenueFullDocument, type GetVenueFullQuery, type GetVenueFullQueryVariables } from "@/graphql/graphql"
import { useNavigate, useParams } from "react-router-dom"
import { usePanelLink } from "@/shared/hooks"
import { useVenueLayoutState } from "./use_venue_layout_state"
import FloorPlanCanvas from "./floor_plan_canvas"
import SectionsList from "./sections_list"
import RemoveSectionDialog from "./remove_section_dialog"
import type { FormValues } from "./types"

const STYLES = {
  loadingContainer: "flex justify-center items-center min-h-screen",
  errorText: "text-red-500 p-8",
  notFound: "p-8",
  pageRoot: "flex flex-col h-full",
  formRoot: "flex-1 flex flex-col overflow-hidden",
  alertMargin: "mb-4",
  contentWrapper: "flex flex-col h-full",
  contentRow: "flex-1 flex gap-4 min-h-0",
} as const

const VenueLayout: React.FC = () => {
  const { venueSlug } = useParams()
  const navigate = useNavigate()
  const linkTo = usePanelLink()

  const {
    data,
    loading,
    error: queryError,
    refetch,
  } = useQuery<GetVenueFullQuery, GetVenueFullQueryVariables>(GetVenueFullDocument, {
    variables: { slug: venueSlug },
  })

  const venue = data?.venue

  useEffect(() => {
    if (venue && !venue.permissions?.manageVenue) {
      navigate(linkTo("settings"))
    }
  }, [venue?.permissions?.manageVenue, navigate, linkTo])

  const {
    error,
    success,
    saving,
    confirmDialog,
    setConfirmDialog,
    setError,
    handleRemoveTableGroup,
    handleRemoveTable,
    handleAddTableGroup,
    handleAddTable,
    handleSubmit,
  } = useVenueLayoutState(venue!, refetch)

  if (loading) {
    return (
      <div className={STYLES.loadingContainer}>
        <CircularProgress />
      </div>
    )
  }

  if (queryError) {
    return <div className={STYLES.errorText}>Error loading venue: {queryError.message}</div>
  }

  if (!venue) {
    return <div className={STYLES.notFound}>Venue not found</div>
  }

  const initialValues: FormValues = {
    tableGroups: venue.tableGroups
      .filter((tg) => !tg.deleted)
      .map((tg) => ({
        id: tg.id,
        name: tg.name,
        deleted: tg.deleted,
        isNew: false,
        tables: venue.tables
          .filter((t) => t.tableGroup.id === tg.id && !t.deleted)
          .map((t) => ({
            id: t.id,
            name: t.name,
            minCapacity: t.minCapacity,
            maxCapacity: t.maxCapacity,
            deleted: t.deleted,
            isNew: false,
            isEditing: false,
          })),
      })),
    plans: venue.plans.map((m) => ({
      id: m.id,
      sectionName: m.sectionName || "",
      plan: m.plan,
      position: m.position,
      default: m.default,
      isNew: false,
    })),
  }

  return (
    <div className={STYLES.pageRoot} data-testid="settings-venue-layout-page">
      <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
        {({ values, setFieldValue, dirty, resetForm }) => (
          <Form className={STYLES.formRoot}>
            {error && (
              <Alert severity="error" className={STYLES.alertMargin} data-testid="settings-venue-layout-alert-error">
                {error}
              </Alert>
            )}
            {success && (
              <Alert
                severity="success"
                className={STYLES.alertMargin}
                data-testid="settings-venue-layout-alert-success"
              >
                {success}
              </Alert>
            )}

            <div className={STYLES.contentWrapper} data-testid="settings-venue-layout-content">
              <div className={STYLES.contentRow}>
                <FloorPlanCanvas
                  dirty={dirty}
                  saving={saving}
                  resetForm={resetForm}
                  confirmDialog={confirmDialog}
                  setConfirmDialog={setConfirmDialog}
                  onError={setError}
                />

                <SectionsList
                  onRemoveTableGroup={(groupIndex) => handleRemoveTableGroup(groupIndex, values, setFieldValue)}
                  onRemoveTable={(groupIndex, tableIndex) =>
                    handleRemoveTable(groupIndex, tableIndex, values, setFieldValue)
                  }
                  onAddTableGroup={() => handleAddTableGroup(values, setFieldValue)}
                  onAddTable={(groupIndex) => handleAddTable(groupIndex, values, setFieldValue)}
                />
              </div>
            </div>
          </Form>
        )}
      </Formik>

      <RemoveSectionDialog confirmDialog={confirmDialog} setConfirmDialog={setConfirmDialog} />
    </div>
  )
}

export default VenueLayout
